/**
 * THOR motion — enter/leave + scroll reveals.
 * Progressive enhancement: content stays visible if Motion CDN is blocked
 * (common in Instagram / Facebook in-app browsers).
 */
(() => {
  if (window.thorMotion) return;

  const isRestrictedWebView = () => {
    const ua = navigator.userAgent || '';
    return /Instagram|FBAN|FBAV|Line\/|MicroMessenger|TikTok/i.test(ua);
  };

  const revealAll = () => {
    document.querySelectorAll('[data-motion-fade]').forEach((el) => {
      el.classList.add('is-inview');
    });
    document.documentElement.classList.remove('has-thor-motion');
  };

  const cssEnter = (el) =>
    new Promise((resolve) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(-10px) scale(0.98)';
      requestAnimationFrame(() => {
        el.style.transition = 'opacity .32s ease, transform .32s cubic-bezier(.22,1,.36,1)';
        el.style.opacity = '1';
        el.style.transform = 'none';
        setTimeout(resolve, 320);
      });
    });

  const cssLeave = (el) =>
    new Promise((resolve) => {
      el.style.transition = 'opacity .2s ease, transform .2s ease';
      el.style.opacity = '0';
      el.style.transform = 'translateY(-8px) scale(0.98)';
      setTimeout(resolve, 200);
    });

  const api = {
    ready: Promise.resolve(),
    enter: cssEnter,
    leave: cssLeave,
    animateInView: () => {},
  };

  window.thorMotion = api;

  const boot = async () => {
    // Instagram / Meta WebViews often block jsDelivr ES modules — skip animation there.
    if (isRestrictedWebView()) {
      revealAll();
      return;
    }

    const safety = window.setTimeout(revealAll, 1500);

    try {
      const motion = await import('https://cdn.jsdelivr.net/npm/motion@12.23.12/+esm');
      window.clearTimeout(safety);
      const { animate, inView, stagger } = motion;

      api.enter = (el) =>
        animate(
          el,
          { opacity: [0, 1], y: [-12, 0], scale: [0.98, 1] },
          { duration: 0.35, easing: [0.22, 1, 0.36, 1] }
        );

      api.leave = (el) =>
        animate(
          el,
          { opacity: 0, y: -8, scale: 0.98 },
          { duration: 0.2, easing: 'ease-out' }
        );

      api.animateInView = (selector = '[data-motion-fade]') => {
        const nodes = [...document.querySelectorAll(selector)];
        if (!nodes.length) return;

        inView(
          nodes,
          (element) => {
            element.classList.add('is-inview');
            animate(
              element,
              { opacity: [0, 1], y: [18, 0] },
              { duration: 0.55, easing: [0.22, 1, 0.36, 1] }
            );
          },
          { amount: 0.2, once: true }
        );
      };

      api.staggerChildren = (parent, childSelector = ':scope > *') => {
        const kids = parent.querySelectorAll(childSelector);
        animate(
          kids,
          { opacity: [0, 1], y: [20, 0] },
          { delay: stagger(0.06), duration: 0.45, easing: [0.22, 1, 0.36, 1] }
        );
      };

      document.documentElement.classList.add('has-thor-motion');
      api.animateInView();
    } catch (err) {
      window.clearTimeout(safety);
      revealAll();
    }
  };

  api.ready = boot();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void api.ready;
    });
  } else {
    void api.ready;
  }
})();
